import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, AlertTriangle, Ban, Layers, CheckCircle2 } from "lucide-react";
import {
  projects,
  weekAllocations,
  accountManagers,
  comerciais,
  type Tipo,
  type Frente,
  TIPOS_PROJETO,
  FRENTES,
} from "@/lib/mock-data";
import { usePeople } from "@/lib/people-store";
import {
  useAiKnowledge,
  ESCOPO_TIPOS,
  WEIGHT_LABELS,
  type EscopoTipo,
} from "@/lib/ai-knowledge-store";
import { computeFit, type FitScope } from "@/lib/fit-engine";
import { useRole } from "@/lib/use-role";

export const Route = createFileRoute("/fit")({
  component: FitPage,
});

const TIPOS: Tipo[] = TIPOS_PROJETO;
const COMPLEXIDADES = ["Baixa", "Média", "Alta"] as const;

const clientesConhecidos = Array.from(new Set(projects.map((p) => p.cliente))).sort();

function FitPage() {
  const role = useRole();
  const readOnly = role === "Comercial";
  const roster = usePeople();
  const knowledge = useAiKnowledge();

  // Dados do novo projeto (mesma base do "Novo card" do Pipeline)
  const [nome, setNome] = useState("");
  const [cliente, setCliente] = useState(clientesConhecidos[0] ?? "");
  const [tipoEntrega, setTipoEntrega] = useState<Tipo>("Full");
  const [frente, setFrente] = useState<Frente>("Apresentações");
  const [amId, setAmId] = useState(accountManagers[0]?.id ?? "");
  const [comercialId, setComercialId] = useState(comerciais[0]?.id ?? "");

  // Escopo enviado à IA
  const [tipo, setTipo] = useState<EscopoTipo>("Slide");
  const [complexidade, setComplexidade] = useState<(typeof COMPLEXIDADES)[number]>("Média");
  const [slides, setSlides] = useState(20);
  const [horas, setHoras] = useState(20);
  const [prazo, setPrazo] = useState("");
  const [fase, setFase] = useState("Conteúdo");

  const [applied, setApplied] = useState<FitScope | null>(null);
  const [alocado, setAlocado] = useState<string | null>(null);

  const fit = useMemo(
    () => (applied ? computeFit(applied, roster, knowledge, weekAllocations) : null),
    [applied, roster, knowledge],
  );

  const totalWeight = Object.values(knowledge.weights).reduce((s, w) => s + w, 0) || 1;

  const canCalc = Boolean(cliente.trim());

  const calcular = () => {
    if (!canCalc) return;
    setAlocado(null);
    setApplied({ cliente: cliente.trim(), tipo, slides, horas, prazo, complexidade, fase });
  };

  return (
    <>
      <TopBar
        title="Fit de Alocação"
        subtitle="Cadastre um novo projeto e receba a sugestão de alocação da IA"
      />
      <div className="flex-1 overflow-auto p-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_1fr]">
          {/* Formulário */}
          <section className="rounded-xl border border-border bg-card">
            <div className="flex items-center gap-2 border-b border-border p-4">
              <div className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary">
                <Layers className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Novo projeto</h2>
                <p className="text-[11px] text-muted-foreground">Mesma base do card do Pipeline</p>
              </div>
            </div>

            <div className="space-y-4 p-4">
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Nome do projeto">
                  <Input
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Ex: Convenção Anual"
                    className="h-8 text-xs"
                  />
                </Fld>
                <Fld label="Cliente *">
                  <Sel value={cliente} onChange={setCliente} options={clientesConhecidos} />
                </Fld>
                <Fld label="Tipo de entrega">
                  <Sel
                    value={tipoEntrega}
                    onChange={(v) => setTipoEntrega(v as Tipo)}
                    options={TIPOS}
                  />
                </Fld>
                <Fld label="Frente">
                  <Sel value={frente} onChange={(v) => setFrente(v as Frente)} options={FRENTES} />
                </Fld>
                <Fld label="Coordenador">
                  <Sel
                    value={amId}
                    onChange={setAmId}
                    options={accountManagers.map((a) => ({ value: a.id, label: a.nome }))}
                  />
                </Fld>
                <Fld label="Comercial">
                  <Sel
                    value={comercialId}
                    onChange={setComercialId}
                    options={comerciais.map((c) => ({ value: c.id, label: c.nome }))}
                  />
                </Fld>
              </div>

              <div>
                <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-primary">
                  Escopo para alocação
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Fld label="Tipo de escopo">
                    <Sel
                      value={tipo}
                      onChange={(v) => setTipo(v as EscopoTipo)}
                      options={ESCOPO_TIPOS}
                    />
                  </Fld>
                  <Fld label="Complexidade">
                    <Sel
                      value={complexidade}
                      onChange={(v) => setComplexidade(v as typeof complexidade)}
                      options={[...COMPLEXIDADES]}
                    />
                  </Fld>
                  <Fld label="Slides">
                    <Input
                      type="number"
                      min={0}
                      value={slides}
                      onChange={(e) => setSlides(Number(e.target.value) || 0)}
                      className="h-8 text-xs"
                    />
                  </Fld>
                  <Fld label="Horas estimadas">
                    <Input
                      type="number"
                      min={0}
                      value={horas}
                      onChange={(e) => setHoras(Number(e.target.value) || 0)}
                      className="h-8 text-xs"
                    />
                  </Fld>
                  <Fld label="Prazo">
                    <Input
                      type="date"
                      value={prazo}
                      onChange={(e) => setPrazo(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </Fld>
                  <Fld label="Fase">
                    <Sel
                      value={fase}
                      onChange={setFase}
                      options={["Conteúdo", "IV", "V1", "Rodada de alteração", "Ensaio"]}
                    />
                  </Fld>
                </div>
              </div>

              <Button
                size="sm"
                className="w-full gap-1.5"
                onClick={calcular}
                disabled={!canCalc || readOnly}
              >
                <Sparkles className="h-3.5 w-3.5" /> Gerar sugestão de alocação
              </Button>
            </div>
          </section>

          {/* Resultado */}
          <section className="rounded-xl border border-border bg-card">
            <div className="flex items-center gap-2 border-b border-border p-4">
              <div className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <h2 className="text-sm font-semibold text-foreground">Sugestão da IA</h2>
                <p className="text-[11px] text-muted-foreground">
                  {applied
                    ? `${applied.cliente} · ${applied.tipo} · ${applied.slides} slides · complexidade ${applied.complexidade.toLowerCase()}`
                    : "Preencha o projeto e clique em gerar sugestão"}
                </p>
              </div>
            </div>

            {!fit ? (
              <div className="p-10 text-center text-xs text-muted-foreground">
                Nenhum escopo enviado ainda.
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5 border-b border-border bg-secondary/20 px-4 py-2">
                  {(Object.keys(WEIGHT_LABELS) as (keyof typeof WEIGHT_LABELS)[]).map((k) => (
                    <span
                      key={k}
                      className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground"
                    >
                      {WEIGHT_LABELS[k]} · {Math.round((knowledge.weights[k] / totalWeight) * 100)}%
                    </span>
                  ))}
                </div>

                {fit.semCandidatoBom && (
                  <div className="flex items-start gap-2 border-b border-border bg-health-yellow/10 p-3 text-[11px] text-muted-foreground">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-health-yellow" />
                    Nenhum profissional atingiu o fit mínimo para este escopo. Revise o prazo,
                    divida o escopo ou enriqueça a Alimentação da IA.
                  </div>
                )}

                <div className="divide-y divide-border">
                  {fit.rows.map((row, idx) => {
                    const person = roster.find((p) => p.id === row.personId);
                    if (!person) return null;
                    return (
                      <div key={row.personId} className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="grid h-10 w-10 place-items-center rounded-full bg-secondary text-sm font-semibold text-foreground">
                              {person.inicial}
                            </div>
                            {idx === 0 && (
                              <span className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-primary text-[8px] font-bold text-primary-foreground">
                                1
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-foreground">
                              {person.nome}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {person.funcao} · {person.senioridade}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-primary">{row.score}%</div>
                            <div className="text-[10px] uppercase text-muted-foreground">fit</div>
                          </div>
                          <Button
                            size="sm"
                            variant={alocado === row.personId ? "secondary" : "outline"}
                            className="h-7 text-xs"
                            disabled={readOnly}
                            onClick={() => setAlocado(row.personId)}
                          >
                            {alocado === row.personId ? (
                              <>
                                <CheckCircle2 className="mr-1 h-3 w-3" /> Alocado
                              </>
                            ) : (
                              "Alocar"
                            )}
                          </Button>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-3">
                          {(Object.keys(WEIGHT_LABELS) as (keyof typeof WEIGHT_LABELS)[]).map(
                            (k) => (
                              <div key={k} className="flex items-center gap-2">
                                <span className="w-28 shrink-0 truncate text-[10px] text-muted-foreground">
                                  {WEIGHT_LABELS[k]}
                                </span>
                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                                  <div
                                    className="h-full rounded-full bg-primary/70"
                                    style={{ width: `${row.breakdown[k]}%` }}
                                  />
                                </div>
                                <span className="w-7 text-right text-[10px] text-foreground">
                                  {row.breakdown[k]}
                                </span>
                              </div>
                            ),
                          )}
                        </div>

                        <ul className="mt-2 flex flex-wrap gap-1.5">
                          {row.reasons.map((r) => (
                            <li
                              key={r}
                              className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground"
                            >
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>

                {fit.excluded.length > 0 && (
                  <div className="border-t border-border p-4">
                    <div className="mb-2 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      <Ban className="h-3 w-3" /> Excluídos
                    </div>
                    <ul className="space-y-1">
                      {fit.excluded.map((e) => {
                        const person = roster.find((p) => p.id === e.personId);
                        return (
                          <li key={e.personId} className="text-[11px] text-muted-foreground">
                            <span className="text-foreground">{person?.nome ?? e.personId}</span> —{" "}
                            {e.motivo}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {alocado && (
                  <div className="border-t border-border bg-primary/10 p-3 text-[11px] text-foreground">
                    Projeto <strong>{nome.trim() || "sem nome"}</strong> para{" "}
                    <strong>{applied?.cliente}</strong> alocado com{" "}
                    <strong>{roster.find((p) => p.id === alocado)?.nome}</strong>. Crie o card no
                    Pipeline para oficializar.
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

function Fld({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-[10px] text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

type Option = string | { value: string; label: string };
function Sel({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Option[];
}) {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
    >
      {opts.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
